import { useEffect, useState } from 'react';

import { BottomSheet } from '@/components/ui/bottom-sheet';
import { ButtonSpinner } from '@/shared/components/button-spinner';
import { cn } from '@/lib/utils';
import { requestKeepVisible } from '@/shared/hooks/use-keep-focused-visible';
import { clearHostedFieldFocus } from '@/shared/hooks/use-visual-viewport';
import { useClaimsStore } from '@/features/claims/store';
import type { ProductPlan } from '@/features/products/types';
import { useCheckout } from '../hooks/use-checkout';
import { PAYRAILS_FIELD_IDS, resetPayrails, validateCardFields } from '../providers/payrails';
import type { PayrailsFieldState } from '../providers/payrails';
import { savingsPercent, strikePrice } from '../domain/pricing';
import { SavingsBadge } from './savings-badge';

interface CardPaymentSheetProps {
  plan: ProductPlan;
  email: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/** Bottom-sheet card payment: Payrails hosted fields + "Proceed payment". */
export function CardPaymentSheet({ plan, email, open, onClose, onSuccess }: CardPaymentSheetProps) {
  // Mount the Payrails hosted fields lazily, once the sheet has finished sliding in
  // (`sheetOpened` ← BottomSheet `onOpened`): only users who actually open the card
  // sheet pay for loading the secure-field iframes (Apple Pay users never do). The
  // session is pre-warmed (`warmPayrails`), so this is just the iframe mounts.
  // Latches true on the first `onOpened` and stays (the mounted fields survive close
  // via `keepMounted` + the provider; leaving checkout unmounts the sheet →
  // `resetPayrails`).
  const [sheetOpened, setSheetOpened] = useState(false);

  const { sdkReady, sdkError, status, error, fieldStates, payWithCard } = useCheckout(
    plan,
    email,
    { theme: 'light', initCard: sheetOpened },
  );

  const savings = savingsPercent(plan);
  const strike = strikePrice(plan);

  const [firstName, setFirstName] = useState(
    () => useClaimsStore.getState().profile.first_name.trim(),
  );
  const [lastName, setLastName] = useState(
    () => useClaimsStore.getState().profile.last_name.trim(),
  );
  // Latches true on the first "Proceed" tap with an incomplete form; that's the only
  // trigger for the red per-field errors (no on-blur / on-keystroke).
  const [showErrors, setShowErrors] = useState(false);

  // Tear down the Payrails form only when the sheet leaves the tree (e.g. navigating off
  // checkout), so a fresh visit re-mounts into a fresh node. It survives open/close.
  useEffect(() => {
    return () => resetPayrails();
  }, []);

  useEffect(() => {
    if (!open) return;
    const { first_name, last_name } = useClaimsStore.getState().profile;
    const first = first_name.trim();
    const last = last_name.trim();
    if (first) setFirstName((current) => current.trim() || first);
    if (last) setLastName((current) => current.trim() || last);
  }, [open]);

  const focusedPayrailsKey = (
    Object.keys(PAYRAILS_FIELD_IDS) as Array<keyof typeof PAYRAILS_FIELD_IDS>
  ).find((key) => fieldStates[key]?.focused);

  // Payrails fields live in cross-origin iframes: `focusin` on the parent is
  // unreliable, but the SDK's FOCUS event is. Key off the focused field, not
  // the whole state object — CHANGE would retrigger scroll on every keystroke.
  // Instagram Android never delivers parent pointerdown/focusin for those
  // iframes and does not resize the WebView, so `requestKeepVisible` also
  // arms the silent keyboard fallback.
  useEffect(() => {
    if (!open) {
      clearHostedFieldFocus();
      return;
    }
    if (!focusedPayrailsKey) {
      const t = window.setTimeout(clearHostedFieldFocus, 160);
      return () => clearTimeout(t);
    }
    const slot = document.getElementById(PAYRAILS_FIELD_IDS[focusedPayrailsKey]);
    if (slot) requestKeepVisible(slot);
  }, [open, focusedPayrailsKey]);

  const handlePay = async () => {
    const first = firstName.trim();
    const last = lastName.trim();
    // Validate every field locally BEFORE tokenize. Empty/invalid → per-field
    // "Fill in the field" hints, no tokenize (which for an empty form would
    // surface Payrails' raw "Error collecting data from the iframe!").
    const card = validateCardFields();
    const allValid = first !== '' && last !== '' && card.cardNumber && card.expiry && card.cvc;
    if (!allValid) {
      setShowErrors(true);
      return;
    }
    const ok = await payWithCard({ firstName: first, lastName: last });
    if (ok) onSuccess();
  };

  // Close via the X / backdrop / Escape: clear the validation state so a reopen starts
  // clean. Entered values survive (keepMounted keeps the names in React state and the
  // card in the Payrails iframe) — only the "attempted submit" flag is reset.
  const handleClose = () => {
    setShowErrors(false);
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      title="Pay with card"
      keepMounted
      onOpened={() => setSheetOpened(true)}
      // Do not resize the overlay around Payrails iframes (they blur). The
      // sheet panel scrolls the focused field above the keyboard instead.
      pinToVisualViewport={false}
      titleClassName="border-0 bg-transparent"
      className="rounded-t-3xl"
    >
      <div className="flex items-center gap-4 bg-white px-4 py-3">
        <span className="text-base font-bold text-mf-neutral-900">Total due today:</span>
        <span className="h-px flex-1 bg-mf-neutral-100" />
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-1">
            {strike != null && (
              <span className="relative text-sm leading-4 text-mf-neutral-400">
                {strike.toFixed(2)}
                <span className="absolute inset-x-0 top-1/2 h-px bg-mf-error" />
              </span>
            )}
            <span className="text-sm font-bold leading-4 text-mf-error">
              {plan.price.toFixed(2)}
            </span>
          </div>
          {savings > 0 && <SavingsBadge percent={savings} />}
        </div>
      </div>

      <div className="flex flex-col gap-6 p-4 pb-6">
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <NameField
              label="First name"
              placeholder="First Name"
              value={firstName}
              onChange={setFirstName}
              submitted={showErrors}
            />
            <NameField
              label="Last name"
              placeholder="Last name"
              value={lastName}
              onChange={setLastName}
              submitted={showErrors}
            />
          </div>

          {/* Payrails secure fields — one small iframe per field (COLLECT), mounted into
              the slots below. The Expires+CVC row is OUR flexbox in the main document and
              every slot reserves its box up front (label + h-[52px]), so nothing depends on a
              cross-origin iframe laying out a multi-field row. Each slot keeps an opaque
              cover until every field reports READY (`sdkReady`), hiding the iframes' white
              bootstrap paint. */}
          {sdkError ? (
            <p className="py-8 text-center text-sm text-mf-error">{sdkError}</p>
          ) : (
            <div className="flex flex-col gap-4">
              <SecureFieldSlot
                label="Card number"
                id={PAYRAILS_FIELD_IDS.cardNumber}
                ready={sdkReady}
                state={fieldStates.cardNumber}
                submitted={showErrors}
              />
              <div className="flex gap-3">
                <SecureFieldSlot
                  label="Expires on"
                  id={PAYRAILS_FIELD_IDS.expiry}
                  ready={sdkReady}
                  state={fieldStates.expiry}
                  submitted={showErrors}
                />
                <SecureFieldSlot
                  label="CVC"
                  id={PAYRAILS_FIELD_IDS.cvc}
                  ready={sdkReady}
                  state={fieldStates.cvc}
                  submitted={showErrors}
                />
              </div>
            </div>
          )}
        </div>

        {error && status === 'error' && (
          <p className="text-center text-sm text-mf-error">{error}</p>
        )}

        <button
          type="button"
          onClick={handlePay}
          disabled={!sdkReady || status === 'processing'}
          className={cn(
            'flex h-14 w-full items-center justify-center rounded-full bg-mf-neutral-900 text-base font-bold text-white transition-opacity hover:opacity-90',
            // The button stays tappable with an empty form on purpose: a tap runs local
            // validation and reveals the per-field hints. Dim only while the fields are
            // still loading; during processing it stays bright and shows the spinner.
            status !== 'processing' && !sdkReady && 'opacity-50',
          )}
        >
          {status === 'processing' ? <ButtonSpinner /> : 'Proceed payment'}
        </button>
      </div>
    </BottomSheet>
  );
}

interface SecureFieldSlotProps {
  label: string;
  id: string;
  ready: boolean;
  state?: PayrailsFieldState;
  submitted: boolean;
}

/** Slot for one Payrails secure-field iframe: our label + bordered box + cover. */
function SecureFieldSlot({ label, id, ready, state, submitted }: SecureFieldSlotProps) {
  // Errors are revealed only after a submit attempt (never while typing or on blur) —
  // then they clear reactively as the field is fixed. Empty → red border + "Fill in the
  // field"; non-empty but not yet complete → red border only (no misleading "Fill in the
  // field"). Focus keeps visual priority over the red border.
  const isEmpty = state?.empty ?? true;
  const emptyError = submitted && isEmpty;
  const error = emptyError || (submitted && !isEmpty && !state?.complete);
  return (
    <div data-keyboard-anchor className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-xs leading-4 text-mf-neutral-600">{label}</span>
      {/* The border + rounded corners are OURS (same tokens as the First/Last name
          inputs), so the corners stay crisp — a border drawn inside the secure iframe
          would be clipped here by the rounded overflow. `overflow-hidden` still clamps
          the borderless iframe to the input height (the SDK reserves a white error-text
          area below it). Focus/invalid come from the field's own events (`state`). */}
      <div
        className={cn(
          'relative h-[52px] overflow-hidden rounded-xl bg-white shadow-[0_0_16px_rgba(26,22,19,0.03)] ring-1 transition-colors',
          state?.focused
            ? 'ring-mf-neutral-900'
            : error
              ? 'ring-mf-error'
              : 'ring-transparent',
        )}
      >
        <div id={id} data-slot="payrails-field" />
        <div
          className={cn(
            'pointer-events-none absolute inset-0 bg-white transition-opacity duration-300',
            ready ? 'opacity-0' : 'opacity-100',
          )}
        >
          <div className="h-[52px] w-full animate-pulse rounded-xl bg-mf-neutral-100" />
        </div>
      </div>
      {emptyError && <FieldError />}
    </div>
  );
}

interface NameFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  submitted: boolean;
}

function NameField({ label, placeholder, value, onChange, submitted }: NameFieldProps) {
  // A name has no "invalid" state — only empty vs filled. The empty error is revealed on
  // submit and clears as soon as the field has text.
  const error = submitted && value.trim() === '';
  return (
    <label data-keyboard-anchor className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-xs leading-4 text-mf-neutral-600">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'h-[52px] w-full min-w-0 rounded-xl bg-white px-4 text-base font-medium text-mf-neutral-900 outline-none placeholder:text-[#c0b7ae] shadow-[0_0_16px_rgba(26,22,19,0.03)] ring-1 transition-colors',
          error ? 'ring-mf-error' : 'ring-transparent focus:ring-mf-neutral-900',
        )}
      />
      {error && <FieldError />}
    </label>
  );
}

function FieldError() {
  return <p className="text-xs text-mf-error">Fill in the field</p>;
}
